import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DowngradeAffectedEmployee } from '../downgrade-validation-dialog/downgrade-validation-dialog.component';
import { ProjectService } from 'src/app/Services/project.service';

export interface PersonalizedSettingsDialogData {
  mode?: 'personalized' | 'global';
  employees?: DowngradeAffectedEmployee[];
  isAdminControlOnly?: boolean;
  targetPlanCode?: string;
  globalViolations?: string[];
}

type DialogStep = 'confirm' | 'processing' | 'result';

@Component({
  selector: 'app-personalized-settings-dialog',
  templateUrl: './personalized-settings-dialog.component.html',
  styleUrls: ['./personalized-settings-dialog.component.css'],
  standalone: false
})
export class PersonalizedSettingsDialogComponent implements OnInit {
  step: DialogStep = 'confirm';
  errorMessage = '';

  settingsForm!: FormGroup;
  selectedEmployees = new Set<string>();

  constructor(
    public dialogRef: MatDialogRef<PersonalizedSettingsDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: PersonalizedSettingsDialogData,
    private fb: FormBuilder,
    private projectService: ProjectService
  ) { }

  minScreenshotInterval = 15;
  advancedTrackingAllowed = false;

  ngOnInit(): void {
    // Select all employees by default if in personalized mode
    if (!this.isGlobalMode() && this.data.employees) {
      this.data.employees.forEach(emp => {
        if (emp.id) {
          this.selectedEmployees.add(String(emp.id));
        }
      });
    }

    const targetPlan = String(this.data.targetPlanCode || '').toLowerCase().trim();
    if (targetPlan === 'business') {
      this.minScreenshotInterval = 5;
      this.advancedTrackingAllowed = true;
    } else if (targetPlan === 'professional') {
      this.minScreenshotInterval = 10;
      this.advancedTrackingAllowed = true;
    } else {
      this.minScreenshotInterval = 15;
      this.advancedTrackingAllowed = false;
    }

    this.settingsForm = this.fb.group({
      ss_screen_capture_time: [this.minScreenshotInterval, [Validators.required]],
      ss_appchange: [{ value: false, disabled: !this.advancedTrackingAllowed }],
      ss_strict_time_tracking: [{ value: false, disabled: !this.advancedTrackingAllowed }],
      ss_automatic_tracking: [{ value: false, disabled: !this.advancedTrackingAllowed }]
    });
  }

  get employeeCount(): number {
    return this.data.employees ? this.data.employees.length : 0;
  }

  get selectedCount(): number {
    return this.selectedEmployees.size;
  }

  get allSelected(): boolean {
    return this.selectedCount > 0 && this.selectedCount === this.employeeCount;
  }

  get selectedEmployeesList(): DowngradeAffectedEmployee[] {
    return this.data.employees ? this.data.employees.filter(emp => this.isEmployeeSelected(emp.id)) : [];
  }

  isGlobalMode(): boolean {
    return this.data.mode === 'global';
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedEmployees.clear();
    } else if (this.data.employees) {
      this.data.employees.forEach(emp => {
        if (emp.id) {
          this.selectedEmployees.add(String(emp.id));
        }
      });
    }
  }

  toggleEmployeeSelection(empId?: number | string): void {
    if (!empId) return;
    const idStr = String(empId);
    if (this.selectedEmployees.has(idStr)) {
      this.selectedEmployees.delete(idStr);
    } else {
      this.selectedEmployees.add(idStr);
    }
  }

  isEmployeeSelected(empId?: number | string): boolean {
    return !!empId && this.selectedEmployees.has(String(empId));
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    if (!this.isGlobalMode() && this.selectedCount === 0) {
      this.errorMessage = 'Please select at least one employee.';
      return;
    }

    if (this.settingsForm.invalid) {
      return;
    }

    this.step = 'processing';
    this.errorMessage = '';

    const formValue = this.settingsForm.getRawValue();

    let captureTime = formValue.ss_screen_capture_time;
    if (typeof captureTime === 'number') {
      const hh = Math.floor(captureTime / 60).toString().padStart(2, '0');
      const mm = (captureTime % 60).toString().padStart(2, '0');
      captureTime = `${hh}:${mm}:00`;
    }

    if (this.isGlobalMode()) {
      // Fetch existing global settings to preserve other values when saving
      this.projectService.getGlobalSettings().pipe(
        switchMap((res: any) => {
          const currentGlobalSettings = (res && res.data) ? res.data : res || {};

          const globalPayload = {
            ...currentGlobalSettings,
          };
          
          if (this.data.isAdminControlOnly) {
            globalPayload.ss_automatic_tracking = formValue.ss_automatic_tracking;
          } else {
            globalPayload.ss_screen_capture_time = captureTime;
            globalPayload.ss_appchange = this.advancedTrackingAllowed ? (formValue.ss_appchange ? 'true' : 'false') : 'false';
            globalPayload.ss_strict_time_tracking = this.advancedTrackingAllowed ? (formValue.ss_strict_time_tracking ? 'true' : 'false') : 'false';
          }
          
          const violations = new Set<string>(this.data.globalViolations || []);
          if (violations.has('Webcam capture enabled')) globalPayload.ss_webcam = 'false';
          if (violations.has('Silent mode enabled')) globalPayload.ss_silent = 'false';
          if (violations.has('Automatic tracking enabled')) globalPayload.ss_automatic_tracking = 'false';
          if (violations.has('App-switch screenshots enabled')) globalPayload.ss_appchange = 'false';
          if (violations.has('Strict time tracking enabled')) globalPayload.ss_strict_time_tracking = 'false';

          return this.projectService.setGlobalSettings(globalPayload, undefined);
        })
      ).subscribe({
        next: () => {
          this.step = 'result';
        },
        error: (err) => {
          this.step = 'confirm';
          this.errorMessage = err?.error?.message || err?.message || 'Failed to update global settings. Please try again.';
        }
      });
    } else {
      const allViolations = new Set<string>();
      (this.data.employees || []).forEach((emp: any) => {
        (emp.details || []).forEach((d: string) => allViolations.add(d));
      });

      const forcedOverrides: any = {};
      if (allViolations.has('Webcam capture enabled')) forcedOverrides.ss_webcam = 'false';
      if (allViolations.has('Silent mode enabled')) forcedOverrides.ss_silent = 'false';
      if (allViolations.has('Automatic tracking enabled')) forcedOverrides.ss_automatic_tracking = 'false';
      if (allViolations.has('App-switch screenshots enabled')) forcedOverrides.ss_appchange = 'false';
      if (allViolations.has('Strict time tracking enabled')) forcedOverrides.ss_strict_time_tracking = 'false';

      const employeesTrue: string[] = [];
      const employeesFalse: string[] = [];
      
      (this.data.employees || []).forEach((emp: any) => {
        if (this.isEmployeeSelected(emp.id)) {
          if (emp.ssPersonalizeSettings) {
            employeesTrue.push(String(emp.id));
          } else {
            employeesFalse.push(String(emp.id));
          }
        }
      });

      const requests = [];

      if (employeesTrue.length > 0) {
        const payloadTrue = {
          team_id: '',
          employee_ids: employeesTrue,
          settings: {
            ss_personalizeSettings: 'true',
            ss_periodic: 'true',
            ss_screen_capture_time: captureTime,
            ss_appchange: this.advancedTrackingAllowed ? (formValue.ss_appchange ? 'true' : 'false') : 'false',
            ss_strict_time_tracking: this.advancedTrackingAllowed ? (formValue.ss_strict_time_tracking ? 'true' : 'false') : 'false',
            ...forcedOverrides
          }
        };
        requests.push(this.projectService.bulkUpdateSettings(payloadTrue));
      }

      if (employeesFalse.length > 0) {
        const payloadFalse = {
          team_id: '',
          employee_ids: employeesFalse,
          settings: {
            ss_personalizeSettings: 'false',
            ss_periodic: 'true',
            ss_screen_capture_time: captureTime,
            ss_appchange: this.advancedTrackingAllowed ? (formValue.ss_appchange ? 'true' : 'false') : 'false',
            ss_strict_time_tracking: this.advancedTrackingAllowed ? (formValue.ss_strict_time_tracking ? 'true' : 'false') : 'false',
            ...forcedOverrides
          }
        };
        requests.push(this.projectService.bulkUpdateSettings(payloadFalse));
      }

      forkJoin(requests).subscribe({
        next: () => {
          this.step = 'result';
        },
        error: (err) => {
          this.step = 'confirm';
          this.errorMessage = err?.error?.message || err?.message || 'Failed to update settings. Please try again.';
        }
      });
    }
  }

  onDone(): void {
    this.dialogRef.close(true);
  }

  getEmployeeTooltip(employee: DowngradeAffectedEmployee): string {
    return employee.role || employee.email?.trim() || employee.name;
  }
}
